'use client';
import {
  Step as MuiStep,
  Stack,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  type TypographyProps,
} from '@unity/cloud-ui';
import React from 'react';

const labelComponentMap = {
  p: (props: Partial<TypographyProps>) => <Typography variant="body1" component="p" {...props} />,
  h3: (props: Partial<TypographyProps>) => <Typography variant="h3" {...props} />,
  h2: (props: Partial<TypographyProps>) => <Typography variant="h2" {...props} />,
};

export type TitleSize = keyof typeof labelComponentMap;

export const Step = (props: { title: string; titleSize?: TitleSize; children: React.ReactNode }) => {
  const { title, titleSize = 'h3', children, ...rest } = props;
  const LabelComponent = labelComponentMap[titleSize];

  return (
    <MuiStep active expanded {...rest}>
      <StepLabel sx={{ '.MuiStepLabel-iconContainer': { zIndex: 0 } }}>
        <LabelComponent>{title}</LabelComponent>
      </StepLabel>
      <StepContent>
        <Stack spacing={1.5}>{children}</Stack>
      </StepContent>
    </MuiStep>
  );
};

export const Steps = (props: {
  titleSize?: 'p' | 'h3' | 'h2';
  children: React.ReactElement<typeof Step>[] | React.ReactElement<typeof Step>;
}) => {
  const childrenWithProps = React.Children.map(props.children, (child) => {
    if (React.isValidElement<typeof Step>(child)) {
      const newStepProps: Partial<typeof Step> = { titleSize: props.titleSize };
      return React.cloneElement(child, newStepProps);
    }
    return child;
  });

  return (
    <Stepper nonLinear orientation="vertical" activeStep={-1}>
      {childrenWithProps}
    </Stepper>
  );
};
